import React from "react"
import { Box, Grid, Switch, Typography } from "@mui/material"
import { dsColorNew } from '@palette'
import { AppProvidersAutocomplete } from "@components/Autocomplete"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"

const emptyRule = () => ({ neverBelow: [], neverAbove: [] })

const ids = arr => (arr ?? []).map(o => o?.value ?? o)

// A brand-image guard, and a different animal from Price Limits in Setup: those are absolute euros
// that never move, this one tracks the market. "Never cheaper than Avis" holds whatever Avis does
// today — if the strategy says 80 and Avis is at 85, the bid stays at 85.
//
// It applies to both pricing strategies, like the undercut: Market Trend and Ranking each produce a
// price, and either can land somewhere the operator does not want to be seen.
//
// Individual providers, not tiers. A tier is a bucket, and "never cheaper than the premium bucket"
// is not a sentence anyone means — the guard is about named brands (Curro, 2026-09-21). This block
// used to offer tiers and used to be unreachable: nothing imported it, and it imported a component
// deleted in b154ce0f, which is why the dangling import never broke the build.
//
// Nothing here is required, so the block publishes no validation: a guard with neither side is
// simply no guard, which is why `competitorRule` is not among the error keys of step 3.
export default function DsRepricingCompetitorRule() {
	const rule = dsRepricingDrawerData.value.competitorRule

	const setRule = next => {
		dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, competitorRule: next }
	}

	const handleToggle = e => {
		setRule(e.target.checked ? emptyRule() : null)
	}

	const handleSide = (key, opts) => setRule({ ...rule, [key]: opts ?? [] })

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: rule ? 2 : 0 }}>
				<Switch checked={!!rule} onChange={handleToggle} size="small"/>
				<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, fontWeight: 600 }}>Never cross these competitors</Typography>
			</Box>

			{rule && (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<AppProvidersAutocomplete
							multiple
							label="Never cheaper than"
							placeholder="No competitor"
							value={ids(rule.neverBelow)}
							onChange={(_, opts) => handleSide('neverBelow', opts)}
						/>
					</Grid>
					<Grid item xs={12}>
						<AppProvidersAutocomplete
							multiple
							label="Never more expensive than"
							placeholder="No competitor"
							value={ids(rule.neverAbove)}
							onChange={(_, opts) => handleSide('neverAbove', opts)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block' }}>
							The bid never crosses the competitors named here, whatever they do today. Both sides are
							optional. Unlike Price Limits in Setup, which are fixed euros, this guard moves with the
							market — and it is applied before those limits, which always have the last word.
						</Typography>
					</Grid>
				</Grid>
			)}
		</Box>
	)
}
